import {
  Box,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Text,
  Tr,
  Button,
  SimpleGrid,
  HStack,
  Spacer,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  Table as TanStackTable,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Legend, Pie, PieChart } from 'recharts';

import {
  date as commonDate,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';

import { DeliverableContainerOutputLenderViewGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  calculateLenderContainerStatus,
  lenderContainerStatusColor,
  lenderContainerStatusIcon,
  LenderStatus,
  LenderStatusCell,
} from '../Status';
import { getDeliverableFinalOutputReceiptsViewUrl } from '../utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { TanStackPaginationControl } from 'packages/common-frontend/components/TanStack';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  getFilterValue,
  ColumnFilter,
  customFullDateStrFilter,
  customMultiFilter,
} from 'packages/deliverables/ui/DeliverablesColumnFilter';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { MultiEntityDrawer as DeliverableEventLogMultiEntityDrawer } from '~/core/routes/deliverables/EntityEventLogDrawers';
import { useGetDeliverablesContainersLenderViewQuery } from '~/core/routes/deliverables/queries/GetDeliverablesContainersLenderView.gql.generated';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { TaskCenterDrawer } from '~/packages/deliverables/ui/lender/TaskCenterDrawer';

const computedStatusSortOrder: { [key in LenderStatus]: number } = {
  overdue: 5,
  readyForReview: 4,
  remediationRequested: 3,
  approved: 2,
  inProgress: 1,
};

const statusOptions = [
  { label: <LenderStatusCell status="overdue" />, value: 'overdue' },
  {
    label: <LenderStatusCell status="remediationRequested" />,
    value: 'remediationRequested',
  },
  {
    label: <LenderStatusCell status="readyForReview" />,
    value: 'readyForReview',
  },
  { label: <LenderStatusCell status="approved" />, value: 'approved' },
  { label: <LenderStatusCell status="inProgress" />, value: 'inProgress' },
];

const filterStatuses = (
  table: TanStackTable<DeliverableContainerOutputLenderViewGQLType>,
  status: string,
) => {
  const column = table.getColumn('calculatedStatus');
  if (column) {
    const currentFilters = getFilterValue(column);
    const index = currentFilters.indexOf(status);
    if (index >= 0) {
      currentFilters.splice(index, 1);
    } else {
      currentFilters.push(status);
    }
    column.setFilterValue(currentFilters);
  }
};

type StatusFilterCardsProps = {
  includesStatus: boolean;
  title: string;
  subtitle?: string;
  count: number;
  status: LenderStatus;
  onClick: () => void;
};
const StatusFilterCards = ({
  includesStatus,
  title,
  subtitle = '',
  count,
  status,
  onClick,
}: StatusFilterCardsProps) => {
  const filterCardSelectedBorderColor = useColorModeValue('black', 'white');
  const filterCardBorderColor = useColorModeValue('gray.100', 'gray.800');

  return (
    <Button
      leftIcon={
        <Icon
          icon={lenderContainerStatusIcon[status]}
          color={`${lenderContainerStatusColor[status]}.300`}
        />
      }
      justifyContent="flex-start"
      whiteSpace="normal"
      py={6}
      variant="outline"
      borderColor={
        includesStatus ? filterCardSelectedBorderColor : filterCardBorderColor
      }
      onClick={onClick}
      _focus={{ boxShadow: 'none' }}
    >
      <HStack width="full">
        <Text>{title}</Text>
        <Text color="gray.500" fontWeight="normal">
          {subtitle}
        </Text>
        <Spacer />
        <Text>{count}</Text>
      </HStack>
    </Button>
  );
};

type DeliverablesReceiptsLenderTableProps = {
  containers: DeliverableContainerOutputLenderViewGQLType[];
};
const DeliverablesReceiptsLenderTable = ({
  containers,
}: DeliverablesReceiptsLenderTableProps) => {
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'calculatedStatus', desc: true },
    { id: 'dueDate', desc: false },
  ]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([
    {
      id: 'calculatedStatus',
      value: ['overdue', 'remediationRequested', 'readyForReview'],
    },
  ]);
  const navigate = useNavigate();

  const statusCounts = useMemo(() => {
    let overdueCount: number = 0;
    let readyForReviewCount: number = 0;
    let remediationRequiredCount: number = 0;
    let approvedCount: number = 0;
    let inProgressCount: number = 0;
    for (const container of containers) {
      const status = calculateLenderContainerStatus(container);
      switch (status) {
        case 'approved':
          approvedCount += 1;
          break;
        case 'inProgress':
          inProgressCount += 1;
          break;
        case 'overdue':
          overdueCount += 1;
          break;
        case 'remediationRequested':
          remediationRequiredCount += 1;
          break;
        case 'readyForReview':
          readyForReviewCount += 1;
          break;
        default:
          finleyAssertUnreachable(status);
          break;
      }
    }

    return {
      overdueCount,
      readyForReviewCount,
      remediationRequiredCount,
      approvedCount,
      inProgressCount,
    };
  }, [containers]);

  const statusFilters = useMemo(() => {
    return _.flatMap(
      columnFilters
        .filter((cf) => cf.id === 'calculatedStatus')
        .map((cf) => cf.value),
    );
  }, [columnFilters]);

  const columns: ColumnDef<
    DeliverableContainerOutputLenderViewGQLType,
    any
  >[] = [
    {
      accessorKey: 'creditFacilityName',
      header: 'Credit Facility',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Credit Facility/Facilities',
      },
    },
    {
      accessorKey: 'configName',
      header: 'Name',
      enableSorting: false,
      cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
    },
    {
      id: 'reportType',
      accessorKey: 'type',
      enableSorting: false,
      header: 'Report Type',
      cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Report Type(s)',
      },
    },
    {
      id: 'dueDate',
      accessorKey: 'dueDate',
      enableSorting: true,
      header: 'Due Date',
      cell: ({ getValue }) =>
        commonDate.fullDateStrToHumanDateString(getValue()),
      filterFn: customFullDateStrFilter,
      meta: {
        filterHeader: 'Select Due Date',
        filterType: 'date',
      },
    },
    {
      id: 'approvedDate',
      enableSorting: true,
      header: 'Approved Date',
      cell: ({ row }) => {
        const { approvedDate } = row.original;

        return approvedDate
          ? commonDate.fullDateStrToHumanDateString(
              commonDate.epochToFullDateStr(approvedDate),
            )
          : '-';
      },
      filterFn: customFullDateStrFilter,
      meta: {
        filterHeader: 'Select Approved Date',
        filterType: 'date',
      },
    },
    {
      id: 'calculatedStatus',
      accessorFn: (row) => calculateLenderContainerStatus(row),
      enableColumnFilter: true,
      header: 'Status',
      // eslint-disable-next-line react/display-name
      cell: ({ row }) => (
        <LenderStatusCell
          status={calculateLenderContainerStatus(row.original)}
        />
      ),
      sortingFn: (rowA, rowB) => {
        const statusRowA = calculateLenderContainerStatus(rowA.original);
        const statusRowB = calculateLenderContainerStatus(rowB.original);
        if (
          computedStatusSortOrder[statusRowA] >
          computedStatusSortOrder[statusRowB]
        ) {
          return 1;
        }
        if (
          computedStatusSortOrder[statusRowA] <
          computedStatusSortOrder[statusRowB]
        ) {
          return -1;
        }
        return 0;
      },
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Status(es)',
        filterOptions: statusOptions,
      },
    },
    {
      id: 'moreDetail',
      enableSorting: false,
      enableColumnFilter: false,
      // eslint-disable-next-line react/display-name
      cell: ({ row }) => {
        const { id, latestReceiptId } = row.original;
        if (!latestReceiptId) {
          return <></>;
        }
        return (
          <Flex padding="0.5rem">
            <IconButtonV2
              aria-label="More details"
              icon={<Icon icon="chevron-right" />}
              variant="v2-ghost"
              onClick={() => {
                navigate(
                  getDeliverableFinalOutputReceiptsViewUrl({
                    containerId: id,
                    receiptId: latestReceiptId,
                  }),
                );
              }}
            />
          </Flex>
        );
      },
      meta: {
        style: {
          padding: '0px',
          textAlign: 'right',
        },
      },
    },
  ];

  const table = useReactTable({
    data: containers,
    columns,
    enableMultiSort: true,
    state: {
      sorting,
      columnFilters,
    },
    initialState: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: 0,
        pageSize: 35,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  const pieChartColor = useColorModeValue(colors.black[70], 'white');
  const tableBorderColor = useColorModeValue(colors.gray[100], 'white');
  const tableHeaderColor = useColorModeValue(colors.black[95], 'white');
  const columnSortColor = useColorModeValue(colors.aurora[400], 'white');
  const tableBodyColor = useColorModeValue('gray.600', 'white');

  return (
    <Box>
      <HStack width="full" spacing="20px">
        {/* Pie Chart! */}
        <PieChart width={500} height={360}>
          <Pie
            width={160}
            height={160}
            data={[
              {
                name: `Overdue`,
                value: statusCounts.overdueCount,
                fill: colors.red[200],
              },
              {
                name: `Remediation Requested`,
                value: statusCounts.remediationRequiredCount,
                fill: colors.orange[400],
              },
              {
                name: `Ready For Review`,
                value: statusCounts.readyForReviewCount,
                fill: colors.orange[300],
              },
              {
                name: `In Progress`,
                value: statusCounts.inProgressCount,
                fill: colors.orange[200],
              },
              {
                name: `Approved`,
                value: statusCounts.approvedCount,
                fill: colors.green[200],
              },
            ]}
            dataKey="value"
            nameKey="name"
          />
          <Legend
            verticalAlign="middle"
            align="right"
            layout="vertical"
            iconType="circle"
            iconSize={8}
            formatter={(value) => (
              <Text color={pieChartColor} display="inline-block" ml="8px">
                {value}
              </Text>
            )}
          />
        </PieChart>

        {/* Hot Buttons / Filter Cards */}
        <SimpleGrid width="full" columns={1} spacing={4} mb={8}>
          <StatusFilterCards
            includesStatus={statusFilters.includes('overdue')}
            title="Overdue"
            count={statusCounts.overdueCount}
            status="overdue"
            onClick={() => {
              filterStatuses(table, 'overdue');
            }}
          />
          <StatusFilterCards
            includesStatus={statusFilters.includes('remediationRequested')}
            title="Remediation Requested"
            count={statusCounts.remediationRequiredCount}
            status="remediationRequested"
            onClick={() => {
              filterStatuses(table, 'remediationRequested');
            }}
          />
          <StatusFilterCards
            includesStatus={statusFilters.includes('readyForReview')}
            title="Ready For Review"
            count={statusCounts.readyForReviewCount}
            status="readyForReview"
            onClick={() => {
              filterStatuses(table, 'readyForReview');
            }}
          />
          <StatusFilterCards
            includesStatus={statusFilters.includes('approved')}
            title="Approved"
            subtitle="past 12 months"
            count={statusCounts.approvedCount}
            status="approved"
            onClick={() => {
              filterStatuses(table, 'approved');
            }}
          />
          <StatusFilterCards
            includesStatus={statusFilters.includes('inProgress')}
            title="In Progress"
            count={statusCounts.inProgressCount}
            status="inProgress"
            onClick={() => {
              filterStatuses(table, 'inProgress');
            }}
          />
        </SimpleGrid>
      </HStack>
      <Table borderWidth="1px" borderColor={tableBorderColor}>
        <Thead>
          {_.map(table.getHeaderGroups(), (headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const columnCanSort = header.column.getCanSort();
                const columnCanFilter = header.column.getCanFilter();

                return (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    textTransform="capitalize"
                    padding={
                      header.column.columnDef.meta?.style?.padding ||
                      '8px 16px 8px 16px'
                    }
                    fontSize="16px"
                    fontWeight="medium"
                    whiteSpace="nowrap"
                    color={tableHeaderColor}
                    width="auto"
                  >
                    <Flex alignItems="center">
                      <Box
                        cursor={columnCanSort ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        mr="8px"
                        color={columnSortColor}
                        fontWeight="thin"
                      >
                        {{
                          asc: <Icon icon="sort-up" />,
                          desc: <Icon icon="sort-down" />,
                        }[header.column.getIsSorted() as string] ??
                          (columnCanSort ? <Icon icon="sort" /> : null)}
                      </Box>
                      <Box>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Box>
                      {columnCanFilter && (
                        <ColumnFilter
                          key={JSON.stringify(getFilterValue(header.column))}
                          type={header.column.columnDef.meta?.filterType}
                          header={header.column.columnDef.meta?.filterHeader}
                          options={header.column.columnDef.meta?.filterOptions}
                          column={header.column}
                          valueFormatFn={
                            header.column.columnDef.meta?.filterFormatFn
                          }
                        />
                      )}
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody color={tableBodyColor} fontSize="sm">
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  textAlign={cell.column.columnDef.meta?.style?.textAlign}
                  padding={cell.column.columnDef.meta?.style?.padding}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
      <TanStackPaginationControl table={table} />
    </Box>
  );
};

// lender page containing a table of all the deliverable they can view
const RouteBase = () => {
  /* 🟢 Borrower/Lender same data */
  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = useMemo(
    () => facilities.map((facility) => facility.id),
    [facilities],
  );

  /* 🔴 Borrower/Lender different data */
  const deliverableContainersQuery = useGetDeliverablesContainersLenderViewQuery(
    { variables: { creditFacilityIds } },
  );

  if (
    !deliverableContainersQuery.data?.deliverablesContainersLenderView ||
    deliverableContainersQuery.loading
  ) {
    return <Skeleton height="600px" />;
  }

  return (
    <>
      <Flex justify="space-between" gap="1rem">
        <Typography variant="page-title" trim>
          Deliverables
        </Typography>
        <Flex gap="1rem">
          <DeliverableEventLogMultiEntityDrawer />
          {/* 🔴 Borrower/Lender different data */}
          <TaskCenterDrawer />
        </Flex>
      </Flex>

      {/* 🔴 Borrower/Lender different data */}
      {deliverableContainersQuery.data.deliverablesContainersLenderView
        .length === 0 ? (
        <Text>No deliverables are currently configured.</Text>
      ) : (
        <DeliverablesReceiptsLenderTable
          containers={
            deliverableContainersQuery.data.deliverablesContainersLenderView
          }
        />
      )}
    </>
  );
};

// Exports
export const Route = () => <RouteBase />;
